/**
 * ggml block geometry: how many elements a block holds, and what it costs.
 *
 * ## Why this table is the only place a wrong number can enter
 *
 * A GGUF tensor's dimensions are element counts, and its byte size is not
 * `numel x width` — quantized ggml types are **blocks**: `Q8_0` stores 32
 * elements in 34 bytes (32 quants plus an f16 scale), `Q4_K` stores 256 in 144.
 * Everything downstream — the byte ledger, bits-per-weight, the map's shading —
 * is a division by a number that comes from here.
 *
 * So the rule from plan §4 binds hardest in this file: **only what is
 * documented**. Every entry below is a published ggml type with a stated block
 * size and type size. A type absent from this table is reported by its id and
 * declines to state a width, rather than being approximated from a neighbouring
 * type that looks similar.
 *
 * ## Unknown types are not hypothetical
 *
 * 498 of 851 tensors in `prism-ml/Ternary-Bonsai-27B-gguf`'s `Q2_0` and `Q2_g64`
 * files are ggml type **42**, and its `PQ2_0` file uses type **142**. That is the
 * majority of those files, so "unknown types are an edge case" is not available
 * as a position.
 *
 * This paragraph said both ids were "the publisher's own, in no published enum",
 * and that is now false of 42 and remains true of 142. Upstream ggml publishes
 * `GGML_TYPE_Q2_0 = 42` at `(64, 18)`; the Bonsai files store *their* type 42 at
 * 2.125 bpw. **The id collides**, which is a different and worse situation than
 * an unpublished id: the name resolves, and resolves to the wrong format.
 *
 * Leaving the old wording would have had this file contradict its own table —
 * the exact failure §27 is about — so it is corrected rather than annotated. The
 * collision is handled where it has to be, in `gguf/inventory.ts`: when the
 * table and the file's layout disagree beyond padding, the layout wins and the
 * disagreement is reported.
 *
 * The reviewer's ruling (2026-08-09) was that the disclosure is **per view, not
 * per model**, on the principle that an unknown type is not an unknown size.
 * That principle holds and is the right one. Measuring it, though, inverted
 * which figure is actually missing:
 *
 * - **Element count is present.** `ne[]` is a format-level field and every one
 *   of the 851 tensors carries it, whatever its type. Params are known.
 * - **Byte size is what the type withholds**, and the layout supplies it: the
 *   gap to the next offset is the extent.
 * - So **bits per weight is computable too**, from two format-level facts. On
 *   the `Q2_0` file it comes out at exactly **2.1250** for all 498 tensors —
 *   68 bytes per 256 elements — which is a sane constant for a 2-bit block
 *   format and is corroboration that both fields mean what they appear to.
 *
 * What an unknown type actually costs us is the **name** and the **decode**, not
 * the arithmetic. So the honest presentation is to render every view, label the
 * type by its id, and disclose the extent of it — rather than blanking cells
 * whose numbers we can state exactly. The one caveat is real but small: a
 * layout-derived extent includes alignment padding (zero on every tensor
 * measured), and the last tensor in a file needs the file's own size.
 */

export interface GgmlType {
  readonly name: string;
  /** Elements per block. 1 for the unquantized types. */
  readonly blockSize: number;
  /** Bytes per block. */
  readonly typeSize: number;
}

/**
 * The published ggml types, by id.
 *
 * Ids 4 and 5 (`Q4_2`, `Q4_3`) and 31-33 (`Q4_0_4_4`, `Q4_0_4_8`, `Q4_0_8_8`)
 * are deliberately absent: they were removed from ggml, and a file still
 * carrying one is better reported as unknown than decoded against a definition
 * that no longer holds.
 *
 * **The ids from 25 up were wrong when this table was first written** — shifted
 * by one, so `BF16` was read as `IQ1_M`, a 2-byte-per-element type decoded as a
 * 256-element block. Nothing in the table itself could reveal that; it was found
 * within minutes by the layout cross-check in `inventory.ts`, because 50 of 79
 * tensors in a file named `…-bf16.gguf` came out at sizes the offsets flatly
 * contradicted. A block-size table is exactly the kind of transcription that
 * looks right and is checked by nothing, which is why the cross-check exists and
 * why a disagreement is surfaced rather than absorbed.
 */
export const GGML_TYPES: ReadonlyMap<number, GgmlType> = new Map([
  [0, { name: "F32", blockSize: 1, typeSize: 4 }],
  [1, { name: "F16", blockSize: 1, typeSize: 2 }],
  [2, { name: "Q4_0", blockSize: 32, typeSize: 18 }],
  [3, { name: "Q4_1", blockSize: 32, typeSize: 20 }],
  [6, { name: "Q5_0", blockSize: 32, typeSize: 22 }],
  [7, { name: "Q5_1", blockSize: 32, typeSize: 24 }],
  [8, { name: "Q8_0", blockSize: 32, typeSize: 34 }],
  /*
   * 36, and **not the 40 that `gguf-py` carries**. Do not "sync" this.
   *
   * `ggml.c` defines `sizeof(block_q8_1) == 2 * sizeof(ggml_half) + QK8_1`,
   * which is 2×2 + 32 = 36 bytes, or 9.00 bits per weight. The struct used to
   * hold two `float`s rather than a packed `ggml_half2`, giving 4+4+32 = 40 and
   * 10.00 — and `gguf-py`'s table still carries that pre-`ggml_half2` figure.
   * Any Q8_1 tensor read through it is overstated by four bytes a block.
   *
   * This row is written from `ggml.c`, the source the format is defined by,
   * rather than from another project's transcription of it. The comment exists
   * because the failure mode is a well-intentioned edit: someone reconciling
   * this table against `gguf-py` would "fix" a correct value into a wrong one,
   * and nothing here would fail — the sizes would simply stop matching the
   * offsets, which is the same silent class the id shift above describes.
   *
   * Found by the reader who reported the MXFP4 defects, who noticed it by
   * comparing the two tools rather than either tool against the spec.
   */
  [9, { name: "Q8_1", blockSize: 32, typeSize: 36 }],
  [10, { name: "Q2_K", blockSize: 256, typeSize: 84 }],
  [11, { name: "Q3_K", blockSize: 256, typeSize: 110 }],
  [12, { name: "Q4_K", blockSize: 256, typeSize: 144 }],
  [13, { name: "Q5_K", blockSize: 256, typeSize: 176 }],
  [14, { name: "Q6_K", blockSize: 256, typeSize: 210 }],
  [15, { name: "Q8_K", blockSize: 256, typeSize: 292 }],
  [16, { name: "IQ2_XXS", blockSize: 256, typeSize: 66 }],
  [17, { name: "IQ2_XS", blockSize: 256, typeSize: 74 }],
  [18, { name: "IQ3_XXS", blockSize: 256, typeSize: 98 }],
  [19, { name: "IQ1_S", blockSize: 256, typeSize: 50 }],
  [20, { name: "IQ4_NL", blockSize: 32, typeSize: 18 }],
  [21, { name: "IQ3_S", blockSize: 256, typeSize: 110 }],
  [22, { name: "IQ2_S", blockSize: 256, typeSize: 82 }],
  [23, { name: "IQ4_XS", blockSize: 256, typeSize: 136 }],
  [24, { name: "I8", blockSize: 1, typeSize: 1 }],
  [25, { name: "I16", blockSize: 1, typeSize: 2 }],
  [26, { name: "I32", blockSize: 1, typeSize: 4 }],
  [27, { name: "I64", blockSize: 1, typeSize: 8 }],
  [28, { name: "F64", blockSize: 1, typeSize: 8 }],
  [29, { name: "IQ1_M", blockSize: 256, typeSize: 56 }],
  [30, { name: "BF16", blockSize: 1, typeSize: 2 }],
  [34, { name: "TQ1_0", blockSize: 256, typeSize: 54 }],
  [35, { name: "TQ2_0", blockSize: 256, typeSize: 66 }],
  /*
   * MXFP4 — one E8M0 scale byte plus 16 nibble bytes per 32 values.
   *
   * 17 × 8 / 32 = 4.25 bits per weight, the same constant `e8m0-scaled.ts`
   * documents and the same figure the safetensors path folds to. This is one
   * format reachable through two containers, so the two must agree, and the
   * arithmetic here is how they do.
   *
   * Its absence was not a sizing failure. The layout inference recovered 4.25
   * correctly from `(blockSize, typeSize)` and then had nothing to call it, so
   * `ggml-org/gpt-oss-20b-GGUF`'s 72 tensors read `type39`, "undocumented" —
   * bits right, name wrong, and an unpublished-type warning on a format this
   * project has supported by name on the other path for weeks.
   *
   * That is the same shape as the `.blocks` separator defect reported beside
   * it: geometry computed correctly, then demoted to a sizer because no string
   * matched. Plan §24 is the structural answer — promoting geometry to an
   * identifier, with abstention where a signature is not unique. This row is
   * the local one, and it is the reason §24 can wait.
   */
  [39, { name: "MXFP4", blockSize: 32, typeSize: 17 }],
  /*
   * NVFP4 — 4-bit elements with one FP8 scale per 32, two 32-value halves to a
   * 64-value block: 2×2 + 32 = 36 bytes, or 4.500 bits per weight.
   *
   * Worth naming by id rather than left to layout inference, which is what the
   * type-39 miss cost: the arithmetic came out right and the format read as
   * `type40`, "undocumented", on a scheme that has a name and a definition.
   */
  [40, { name: "NVFP4", blockSize: 64, typeSize: 36 }],
  /*
   * Q1_0 and Q2_0 — 128 and 64 values a block, 18 bytes each, so 1.125 and
   * 2.250 bits per weight. Both geometries are unique in this table, which is
   * the property `ggml-types.test.ts` pins rather than assumes: a duplicate
   * would silently enlarge the abstain list that plan §24 is scoped around.
   */
  [41, { name: "Q1_0", blockSize: 128, typeSize: 18 }],
  [42, { name: "Q2_0", blockSize: 64, typeSize: 18 }],
]);

/**
 * What a tensor of `numel` elements costs in this type, or `undefined` if the
 * type is not one we have a published definition for.
 *
 * `undefined` is a real answer here and callers must handle it — see the module
 * comment. Returning a guess would put a wrong number in the one place the
 * project's whole claim rests on.
 */
export function ggmlByteLength(typeId: number, numel: number): number | undefined {
  const type = GGML_TYPES.get(typeId);
  if (!type) return undefined;
  /*
   * Blocks, rounded up. A row that is not a whole number of blocks is not
   * something ggml writes — llama.cpp requires the contiguous axis to divide by
   * the block size — but rounding up is the honest direction if one ever
   * appears, and `inventory.ts` cross-checks every size against the layout
   * anyway, so a disagreement is reported rather than absorbed.
   */
  return Math.ceil(numel / type.blockSize) * type.typeSize;
}

/**
 * A display name for a type id.
 *
 * Unknown ids are named by their number rather than by a guess, and the name is
 * deliberately ugly — `type42` should read as a fact about the file, not as a
 * format anyone recognises.
 */
export function ggmlTypeName(typeId: number): string {
  return GGML_TYPES.get(typeId)?.name ?? `type${typeId}`;
}

export function isKnownGgmlType(typeId: number): boolean {
  return GGML_TYPES.has(typeId);
}

/**
 * The block layout of a named ggml type, for callers holding a *name* rather
 * than a type id.
 *
 * The inventory records the type's name, not its id, so a consumer that wants
 * to say something specific about a dtype — "256 weights in 98 bytes" — has to
 * come back the other way. `undefined` for anything not in the table, which is
 * the honest answer and the one every caller here already handles.
 */
export function ggmlTypeByName(name: string): GgmlType | undefined {
  for (const type of GGML_TYPES.values()) if (type.name === name) return type;
  return undefined;
}

/**
 * Bits per weight a ggml type costs, from its own block layout.
 *
 * Derived rather than tabulated. A second table of "IQ3_XXS is 3.06 bits" would
 * be a claim that could drift from `GGML_TYPES`, which is the one place the
 * layout is stated — and drifting *quietly*, since nothing would recompute it.
 */
export function ggmlBitsPerWeight(name: string): number | undefined {
  const type = ggmlTypeByName(name);
  return type === undefined ? undefined : (type.typeSize * 8) / type.blockSize;
}
